import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { sanitizeNumericInput } from '../../utils/validation';

/**
 * AgeVerifyStep — Date of birth input (MM/DD/YYYY) with 18+ validation.
 *
 * @param {Object} props
 * @param {Object} props.dob - { day, month, year }
 * @param {function} props.onDobChange - Called with updated dob object
 * @param {function} props.onContinue - Called when age is verified (18+)
 * @param {function} props.onBack - Called to go back
 * @returns {JSX.Element}
 */
function AgeVerifyStep({ dob, onDobChange, onContinue, onBack }) {
  const [error, setError] = useState(null);
  const dayRef = useRef(null);
  const yearRef = useRef(null);

  const handleDobField = (field, value, maxLength) => {
    const sanitized = sanitizeNumericInput(value, maxLength);
    onDobChange({ ...dob, [field]: sanitized });
    if (error) setError(null);

    // Auto-advance: MM → DD → YYYY
    if (field === 'month' && sanitized.length === 2) {
      dayRef.current?.focus();
    } else if (field === 'day' && sanitized.length === 2) {
      yearRef.current?.focus();
    }
  };

  const isAtLeast18 = (day, month, year) => {
    const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 18;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const { day, month, year } = dob;

    if (!day || !month || !year) {
      setError('Please enter your full date of birth');
      return;
    }

    const d = parseInt(day);
    const m = parseInt(month);
    const y = parseInt(year);

    if (m < 1 || m > 12) {
      setError('Please enter a valid month (01-12)');
      return;
    }

    if (d < 1 || d > 31) {
      setError('Please enter a valid day (01-31)');
      return;
    }

    if (y < 1900 || y > new Date().getFullYear()) {
      setError('Please enter a valid year');
      return;
    }

    if (!isAtLeast18(day, month, year)) {
      setError('You must be at least 18 years old to use Polar Ace');
      return;
    }

    setError(null);
    onContinue(dob);
  };

  return (
    <div className="auth-step age-verify-step">
      <button type="button" className="auth-step__back" onClick={onBack} aria-label="Go back">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
      </button>

      <h2 className="auth-step__title">Verify your age</h2>
      <p className="auth-step__subtitle">You must be at least 18 to use Polar Ace</p>

      <form onSubmit={handleSubmit} className="auth-step__form">
        <div className="auth-input-group">
          <label className="auth-input__label">Date of Birth</label>
          <div className="dob-inputs">
            <input
              type="text"
              className={`auth-input dob-input ${error ? 'auth-input--error' : ''}`}
              placeholder="MM"
              value={dob.month}
              onChange={(e) => handleDobField('month', e.target.value, 2)}
              maxLength={2}
              inputMode="numeric"
              autoFocus
            />
            <input
              type="text"
              className={`auth-input dob-input ${error ? 'auth-input--error' : ''}`}
              placeholder="DD"
              value={dob.day}
              onChange={(e) => handleDobField('day', e.target.value, 2)}
              maxLength={2}
              inputMode="numeric"
              ref={dayRef}
            />
            <input
              type="text"
              className={`auth-input dob-input dob-input--year ${error ? 'auth-input--error' : ''}`}
              placeholder="YYYY"
              value={dob.year}
              onChange={(e) => handleDobField('year', e.target.value, 4)}
              maxLength={4}
              inputMode="numeric"
              ref={yearRef}
            />
          </div>
          {error && <p className="auth-input__error">{error}</p>}
        </div>

        <button
          type="submit"
          className="auth-continue-btn"
          disabled={!dob.day || !dob.month || !dob.year || dob.year.length < 4}
        >
          Continue
        </button>
      </form>
    </div>
  );
}

AgeVerifyStep.propTypes = {
  dob: PropTypes.shape({
    day: PropTypes.string.isRequired,
    month: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
  }).isRequired,
  onDobChange: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default AgeVerifyStep;
